import { get as getCookie } from 'js-cookie';
import { isArray } from 'lodash';
import moment from 'moment';

export * from './helpers/array';
export * from './helpers/css';
export * from './helpers/date';
export * from './helpers/enum';
export * from './helpers/sxProps';

// String
export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const convertToStringOrNull = (newValue: unknown): string | null => {
  if (
    newValue === null ||
    (typeof newValue === 'string' && newValue.length > 0)
  ) {
    return newValue;
  }

  if (typeof newValue === 'number') {
    return newValue.toString();
  }

  return null;
};

// Translation
/**
 * The Prefix & Suffix used in legacy UI translations interpolation
 * uses single curly brackets where UI2 uses double curly brackets
 */
export const legacyTranslationInterpolation = {
  prefix: '{',
  suffix: '}',
};

// Number
const mathToDecimals = (
  value: number,
  decimals: number,
  mathFunction: (value: number) => number,
) => {
  const multiplier = Math.pow(10, decimals);
  return mathFunction(value * multiplier) / multiplier;
};

export const mathRoundToDecimals = (value: number, decimals: number) => {
  return mathToDecimals(value, decimals, Math.round);
};

export const mathFloorToDecimals = (value: number, decimals: number) => {
  return mathToDecimals(value, decimals, Math.floor);
};

export const regex_IsNumber = new RegExp(`^\\d+(\\.\\d+)?$`, 'g');

export const isValidPhoneNumber = (
  phone: string | null | undefined,
): boolean => {
  /**
   * Phone number RegEx
   * 1) Starts with any number, or + and any number other than 0
   * 2) Ends in a number
   * 3) Consists of numbers or allowed special characters:
   *      brackets, dot, dash, slash or whitespace
   *
   * TODO The rule should be more strict and has some issues,
   *  e.g. currently allows consecutive special characters,
   *  and incorrectly placed brackets.
   *  However, phone number practices are varied
   *  and we don't want to be too strict.
   */
  const PhoneRegEx = /^(\d|\+[1-9])([()/\s.\-\d]{3,28})\d$/;
  return (
    !!phone && PhoneRegEx.test(phone) && phone?.length > 5 && phone.length <= 30
  );
};

export const isValidOptionalPhoneNumber = (
  phone: string | null | undefined,
): boolean => {
  return phone?.length ? isValidPhoneNumber(phone) : true;
};

export const getRegex_IsNumberToDecimals = (decimals: number) =>
  new RegExp(`^\\d+(\\.\\d{1,${decimals}})?$`, 'g');

export const getNumberOrUndefined = (
  value: number | string | undefined,
): number | undefined => {
  switch (typeof value) {
    case 'number':
      return value;
    case 'string':
      const parsed = parseInt(value);

      return !isNaN(parsed) ? parsed : undefined;
    case 'undefined':
    default:
      return undefined;
  }
};

// Attachments
export const getAttachmentUrl = (empId: string, attachmentId: number) =>
  `/d/bin/employee/${empId}/file/${attachmentId}`;

export const getProfilePictureUrl = (pictureId: string) =>
  `/d/bin/employee/profile/picture/${pictureId}`;

// Misc
export const nullAsUndefined = <T>(value: T): T | undefined =>
  value === null ? undefined : value;

// Attachments
export const getEmployeeAttachmentUrl = (
  employeeId: number,
  attachmentId: number,
) => {
  return `${window.origin}/d/bin/employee/${employeeId}/file/${attachmentId}`;
};

export const getCompanyFormAttachmentUrl = (
  employeeId: number,
  attachmentId: number,
) => {
  return `${window.origin}/d/bin/company/form/${employeeId}/file/${attachmentId}`;
};

// File Export
export const getExportFileName = (props: {
  title: string;
  startDate?: Date;
  endDate?: Date;
}) => {
  const { title, startDate, endDate } = props;

  const formatDate = (date: Date) => moment(date).format('YYYYMMDD');

  const exportFileName = title
    .replace(/[^a-z0-9äöüÄÖÜß]/gi, '_')
    .replace(/_{2,}/g, '_');

  if (!!startDate && !!endDate) {
    return `${exportFileName}-${formatDate(startDate)}-${formatDate(endDate)}`;
  } else if (!!startDate) {
    return `${exportFileName}-${formatDate(startDate)}`;
  }

  return exportFileName;
};

export const openInNewTab = (url: string) => {
  window.open(url, '_blank')?.focus();
};

export const isMobileBrowser = () =>
  /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );

export const cookieEnabledTestFeature = (featureKey: string) => {
  const cookie = getCookie('enableTestFeature');

  if (!!cookie) {
    const featureKeys = cookie.split(',').map((s) => s.trim());

    // Is Feature Test Enabled
    return !!featureKey && featureKeys.includes(featureKey);
  }

  return false;
};

export const alertOnDebug = (message?: string, scope?: string) => {
  const cookie = getCookie('alertOnDebug');

  if (!!cookie) {
    const conditions = cookie.split(',').map((s) => s.trim());
    const showAlert =
      (!!scope && conditions.includes(scope)) || conditions[0] === 'all';

    if (showAlert) {
      alert(message ?? scope ?? '');
    }
  }
};

export const redirectToOrigin = (message?: string) => {
  // For debugging what causes redirect:
  alertOnDebug(
    message ?? 'Redirecting user to starting page',
    'redirectToOrigin',
  );
  window.location.replace(window.location.origin);
};

export const redirectToApplicationConsole = (message?: string) => {
  // For debugging what causes redirect:
  alertOnDebug(
    message ?? 'Redirecting user to starting page',
    'redirectToApplicationConsole',
  );
  window.location.replace(`${window.location.origin}/d/html/index.html`);
};

export const redirectToLogOut = (message?: string) => {
  // For debugging what causes redirect:
  alertOnDebug(
    message ?? 'Redirecting user to starting page',
    'redirectToLogOut',
  );
  window.location.replace('/page-logout');
};

export const validText = (text: string) => {
  if (text === '' || text.length < 1) {
    return false;
  } else {
    return true;
  }
};

// Custom Console Logging

const styledConsoleLog = (
  message: string | string[],
  type: 'error' | 'success' | 'info',
  payload?: unknown,
) => {
  const styles = {
    info: `background-color: #00f; color: #fff; font-weight: bold;`,
    error: `background-color: #f00; color: #fff; font-weight: bold;`,
    success: `background-color: #008000; color: #fff; font-weight: bold;`,
  };

  if (typeof payload !== 'undefined') {
    console.log(
      `%c ${isArray(message) ? message.join(' \r\n ') : message} `,
      styles[type],
      `\r\n`,
      payload,
    );
  } else {
    console.log(
      `%c ${isArray(message) ? message.join(' \r\n ') : message} `,
      styles[type],
    );
  }
};

export const browserLogError = (
  message: string | string[],
  payload?: unknown,
) => styledConsoleLog(message, 'error', payload);

export const browserLogInfo = (message: string | string[], payload?: unknown) =>
  styledConsoleLog(message, 'info', payload);

export const browserLogSuccess = (
  message: string | string[],
  payload?: unknown,
) => styledConsoleLog(message, 'success', payload);
